import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import { Title, Card, Pill, Modal, Field, Err, money } from './gui.jsx'
import { ROLES, watchEmployees, createEmployee, updateEmployee, clockIn, clockOut, clockStatus, deleteEmployee } from '../../lib/employees.js'
import { watchSales } from '../../lib/sales.js'
import { getCurrentEmployeeId, setCurrentEmployeeId, useCurrentRole } from '../../lib/roles.js'

export default function GEmployees() {
  const [raw, setRaw] = useState({})
  const [sales, setSales] = useState({})
  const [edit, setEdit] = useState(null)
  const [adding, setAdding] = useState(false)
  const [current, setCurrent] = useState(getCurrentEmployeeId())
  const { can } = useCurrentRole()
  useEffect(() => { const a = watchEmployees(setRaw), b = watchSales(setSales); return () => { a(); b() } }, [])
  const rows = useMemo(() => Object.values(raw || {}).filter(Boolean).sort((a, b) => (a.name || '').localeCompare(b.name || '')), [raw])
  const salesBy = useMemo(() => { const m = {}; Object.values(sales || {}).forEach((s) => { if (s?.employeeId) m[s.employeeId] = (m[s.employeeId] || 0) + (Number(s.total) || 0) }); return m }, [sales])

  return (
    <GShell>
      <Title title="Employees" subtitle="Roles, clock in/out, sales & commission." right={can('manager') ? <button className="btn-primary text-xs" onClick={() => setAdding(true)}>+ Add Employee</button> : null} />

      <Card>
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Acting as (drives POS attribution & permissions)</div>
        <select className="input" value={current || ''} onChange={(e) => { setCurrentEmployeeId(e.target.value); setCurrent(e.target.value) }}>
          <option value="">— owner (full access) —</option>
          {rows.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.role})</option>)}
        </select>
      </Card>

      {!can('manager') ? <Card style={{ borderColor: '#F9731655' }}><span className="text-xs" style={{ color: '#C2410C' }}>You're acting as a staff role — add/edit/delete is limited to manager+.</span></Card> : null}

      <section className="pb-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.length === 0 ? <p className="text-sm text-gray-500">No employees yet.</p> : rows.map((e) => {
          const { open } = clockStatus(e)
          const attributed = salesBy[e.id] || 0
          const commission = Math.round(attributed * (Number(e.commissionPct) || 0) / 100 * 100) / 100
          return (
            <Card key={e.id} style={open ? { borderColor: '#15803D55' } : {}}>
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-extrabold" style={{ color: 'var(--ge-primary)' }}>{e.name}</div>
                  <div className="text-xs text-gray-500">{e.role}{e.commissionPct ? ` · ${e.commissionPct}% comm` : ''}</div></div>
                {open ? <Pill bg="rgba(21,128,61,0.14)" fg="#15803D">Working</Pill> : <Pill>Off</Pill>}
              </div>
              <div className="text-xs text-gray-500 mt-1">Sales attributed: {money(attributed)} · Commission: {money(commission)}</div>
              <div className="mt-2 flex flex-wrap gap-1 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                {open ? <button className="btn-secondary text-xs" onClick={() => clockOut(e.id, open.id)}>Clock Out</button> : <button className="btn-primary text-xs" onClick={() => clockIn(e.id)}>Clock In</button>}
                {can('manager') ? <button className="btn-ghost text-xs" onClick={() => setEdit(e)}>Edit</button> : null}
                {can('owner') ? <button className="btn-ghost text-xs ml-auto" style={{ color: '#B91C1C' }} onClick={async () => { if (window.confirm(`Delete ${e.name}?`)) await deleteEmployee(e.id) }}>Delete</button> : null}
              </div>
            </Card>
          )
        })}
      </section>

      {(adding || edit) ? <EmpModal e={edit} onClose={() => { setAdding(false); setEdit(null) }} /> : null}
    </GShell>
  )
}

function EmpModal({ e, onClose }) {
  const [f, setF] = useState(e || { name: '', role: 'staff', commissionPct: '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const save = async () => { setBusy(true); setErr(''); try { e ? await updateEmployee(e.id, f) : await createEmployee(f); onClose() } catch (er) { setErr(er?.message || 'Failed'); setBusy(false) } }
  return (
    <Modal title={e ? `Edit — ${e.name}` : 'Add Employee'} onClose={onClose}>
      <div className="grid sm:grid-cols-3 gap-2">
        <Field label="Name"><input className="input w-full" value={f.name || ''} onChange={(ev) => setF((s) => ({ ...s, name: ev.target.value }))} /></Field>
        <Field label="Role"><select className="input w-full" value={f.role} onChange={(ev) => setF((s) => ({ ...s, role: ev.target.value }))}>{ROLES.map((r) => <option key={r}>{r}</option>)}</select></Field>
        <Field label="Commission %"><input className="input w-full" type="number" value={f.commissionPct ?? ''} onChange={(ev) => setF((s) => ({ ...s, commissionPct: ev.target.value }))} /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-3"><button className="btn-ghost text-sm" onClick={onClose}>Cancel</button><button className="btn-primary text-sm" onClick={save} disabled={busy || !String(f.name || '').trim()}>{busy ? 'Saving…' : 'Save'}</button></div>
      <Err msg={err} />
    </Modal>
  )
}
